import React, { useEffect, useState, useCallback } from "react";
import { useLang } from "@/App";
import { legal } from "@/lib/legal";

const KEY = "neurotheos_cookie_consent_v2";
const REOPEN_EVENT = "neurotheos:open-cookie-settings";

// Public helper so any component can re-open the banner
export function openCookieSettings() {
  window.dispatchEvent(new Event(REOPEN_EVENT));
}

export default function CookieBanner() {
  const { lang } = useLang();
  const L = legal[lang].cookieBanner;

  const [show, setShow] = useState(false);
  const [view, setView] = useState("banner"); // "banner" | "customize"
  const [prefs, setPrefs] = useState({
    essential: true, // locked on
    preferences: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const choice = localStorage.getItem(KEY);
    if (!choice) setShow(true);
    const onOpen = () => {
      try {
        const saved = JSON.parse(localStorage.getItem(KEY) || "null");
        if (saved?.prefs) setPrefs({ ...saved.prefs, essential: true });
      } catch (_) { /* ignore parse errors */ }
      setView("customize");
      setShow(true);
    };
    window.addEventListener(REOPEN_EVENT, onOpen);
    return () => window.removeEventListener(REOPEN_EVENT, onOpen);
  }, []);

  const persist = useCallback((finalPrefs) => {
    localStorage.setItem(
      KEY,
      JSON.stringify({
        prefs: finalPrefs,
        at: new Date().toISOString(),
        version: 2,
      })
    );
    setShow(false);
    setView("banner");
  }, []);

  const acceptAll = () => persist({ essential: true, preferences: true, analytics: true, marketing: true });
  const rejectAll = () => persist({ essential: true, preferences: false, analytics: false, marketing: false });
  const saveChoices = () => persist({ ...prefs, essential: true });

  if (!show) return null;

  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const containerCls = view === "customize"
    ? "fixed inset-0 z-[60] flex items-center justify-center p-4"
    : "fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[60]";

  const panelStyle = view === "customize"
    ? { background: "#0f0f0f", color: "#fbf8f1", border: "1px solid #2a2a22", maxWidth: 580, width: "100%", maxHeight: "85vh", overflowY: "auto" }
    : { background: "#0f0f0f", color: "#fbf8f1", border: "1px solid #2a2a22" };

  return (
    <div className={containerCls} role="dialog" aria-label="cookie consent" data-testid="cookie-banner">
      {view === "customize" && (
        <div className="fixed inset-0" style={{ background: "rgba(10,10,8,0.7)", backdropFilter: "blur(4px)" }} onClick={() => setView("banner")} />
      )}

      <div className="relative rounded-2xl p-5 md:p-6 shadow-2xl" style={panelStyle}>
        {view === "banner" && (
          <>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "#c9a961", color: "#0a0a0a" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 1 0 10 10 5 5 0 0 1-5-5 5 5 0 0 1-5-5z" /><circle cx="8.5" cy="10" r="1.2" fill="#0a0a0a" /><circle cx="14" cy="14" r="1.2" fill="#0a0a0a" /><circle cx="10" cy="15" r="0.8" fill="#0a0a0a" /></svg>
              </div>
              <div className="flex-1">
                <div className="serif" style={{ fontSize: 18 }}>{L.title}</div>
                <p className="mt-1 text-xs leading-relaxed" style={{ color: "#bdb7a3" }}>{L.body}</p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    onClick={acceptAll}
                    className="btn-pill w-full"
                    style={{ background: "#fbf8f1", color: "#0a0a0a", padding: "10px 12px", fontSize: 11, letterSpacing: "0.05em" }}
                    data-testid="cookie-accept-all"
                  >
                    {L.accept}
                  </button>
                  <button
                    onClick={rejectAll}
                    className="btn-pill w-full"
                    style={{ background: "#fbf8f1", color: "#0a0a0a", padding: "10px 12px", fontSize: 11, letterSpacing: "0.05em" }}
                    data-testid="cookie-reject-all"
                  >
                    {L.reject}
                  </button>
                  <button
                    onClick={() => setView("customize")}
                    className="btn-pill w-full"
                    style={{ background: "transparent", color: "#fbf8f1", border: "1px solid #cfcab6", padding: "10px 12px", fontSize: 11, letterSpacing: "0.05em" }}
                    data-testid="cookie-customize"
                  >
                    {L.customize}
                  </button>
                </div>
                <div className="mt-3 text-[11px]">
                  <a href="/legal/cookies" className="underline" style={{ color: "#c9a961" }} data-testid="cookie-learn-more">{L.learn} →</a>
                </div>
              </div>
            </div>
          </>
        )}

        {view === "customize" && (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="serif" style={{ fontSize: 22 }}>{L.customize}</div>
                <p className="text-xs mt-1" style={{ color: "#bdb7a3", maxWidth: 460 }}>{L.body}</p>
              </div>
              <button onClick={() => setView("banner")} aria-label="close" data-testid="cookie-close-customize" className="text-sm" style={{ color: "#cfcab6" }}>✕</button>
            </div>

            <div className="space-y-3">
              {["essential", "preferences", "analytics", "marketing"].map((cat) => {
                const c = L.categories[cat];
                const required = c.required;
                const on = required ? true : prefs[cat];
                return (
                  <div key={cat} className="rounded-xl p-4 flex items-start justify-between gap-4" style={{ background: "#15140f", border: "1px solid #2a2a22" }}>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <div className="serif" style={{ fontSize: 16 }}>{c.name}</div>
                        {required && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: "#2a2a22", color: "#c9a961" }}>{lang === "ro" ? "Mereu activ" : "Always on"}</span>}
                      </div>
                      <p className="text-xs mt-1 leading-relaxed" style={{ color: "#9b9582" }}>{c.desc}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => !required && setPrefs((p) => ({ ...p, [cat]: !p[cat] }))}
                      disabled={required}
                      aria-pressed={on}
                      data-testid={`cookie-toggle-${cat}`}
                      className="relative shrink-0"
                      style={{
                        width: 44,
                        height: 24,
                        borderRadius: 999,
                        background: on ? "#c9a961" : "#3a3a30",
                        opacity: required ? 0.6 : 1,
                        cursor: required ? "not-allowed" : "pointer",
                        transition: "background .2s",
                      }}
                    >
                      <span
                        style={{
                          position: "absolute",
                          top: 2,
                          left: on ? 22 : 2,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background: "#fbf8f1",
                          transition: "left .2s",
                        }}
                      />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button onClick={rejectAll} className="btn-pill" style={{ background: "transparent", color: "#fbf8f1", border: "1px solid #cfcab6", padding: "10px 14px", fontSize: 11 }} data-testid="cookie-reject-all-in-modal">{L.reject}</button>
              <button onClick={saveChoices} className="btn-pill" style={{ background: "transparent", color: "#fbf8f1", border: "1px solid #c9a961", padding: "10px 14px", fontSize: 11 }} data-testid="cookie-save-choices">{L.save}</button>
              <button onClick={acceptAll} className="btn-pill" style={{ background: "#fbf8f1", color: "#0a0a0a", padding: "10px 14px", fontSize: 11 }} data-testid="cookie-accept-all-in-modal">{L.accept}</button>
            </div>
            <div className="mt-3 text-[11px]">
              <a href="/legal/cookies" className="underline" style={{ color: "#c9a961" }}>{L.learn} →</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
